import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';

import { GuidebookPhotoKind } from 'src/shared/services/guidebook-address-fields';
import {
  FREE_PARKING_SPOT_OPTIONS,
  MAX_PARKING_COST_OPTIONS,
  PAID_PARKING_SPOT_OPTIONS,
  PARKING_PAY_OPTIONS,
  PARKING_TYPE_OPTIONS,
  ParkingOption,
} from 'src/shared/constants/parking-options';

export interface AddressSetting {
  id: number;
  key: string;
  data: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface UpsertGuidebookEntry {
  key: string;
  /** Required by the backend — an entry without `data` is rejected. Removal goes through `deleteGuidebookSettings`. */
  data: Record<string, unknown>;
}

export interface AddressWithSettings {
  id: number;
  address_name?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  address_settings?: AddressSetting[];
  [key: string]: unknown;
}

export interface ReservationSummary {
  id: number;
  uuid?: string;
  check_in_date?: string;
  check_out_date?: string;
  [key: string]: unknown;
}

export const CUSTOM_SECTION_KEY_PREFIX = 'custom_';

export const SECTION_ORDER_KEY = 'guidebook_section_order';

/**
 * Domain every guidebook setting belongs to. address_settings is a shared multi-domain
 * key/value store; the backend requires this top-level `category` to scope the row.
 */
export const GUIDEBOOK_SETTING_CATEGORY = 'guidebook';

export interface CustomSectionData {
  label: string;
  value: string;
  visible_to_guest: boolean;
  deleted?: boolean;
}

export type GuidebookV2CategoryId =
  | 'parking'
  | 'access'
  | 'connectivity'
  | 'stay_details'
  | 'emergencies'
  | 'contacts'
  | 'property_reference'
  | 'appliances'
  | 'checkout';

export interface GuidebookV2Field {
  /** Setting key persisted in address_settings (or a synthetic id for AddressModel-backed fields). */
  key: string;
  /** Display label shown in the editor and rendered as the section title for guests. */
  label: string;
  /** Input style hint. */
  inputType?: 'text' | 'textarea' | 'select';
  /** When true the value is sourced from AddressModel (not address_settings). */
  fromAddressModel?: boolean;
  /**
   * AddressModel column name when fromAddressModel is true. Parking and access name the
   * pro column; rental accounts read the guest_* twin (see guidebookAddressField).
   */
  addressField?: string;
  /** Choices for select fields. */
  options?: ParkingOption[];
  /** Choices that replace `options` once the parking is paid. */
  optionsWhenPaid?: ParkingOption[];
  /** Only relevant (shown and kept) when the parking is paid. */
  onlyWhenPaid?: boolean;
}

export interface GuidebookV2Category {
  id: GuidebookV2CategoryId;
  label: string;
  icon: string;
  fields: GuidebookV2Field[];
  /**
   * Fixed cards mirror Edit Property: always shown to guests, never removed, and they
   * take no free-form rows. They can still be reordered.
   */
  fixed?: boolean;
  /** Edit Property photos shown on the card. */
  photoKind?: GuidebookPhotoKind;
}

/** Field key of the free/paid choice the other parking fields depend on. */
export const PAID_PARKING_FIELD_KEY = 'paid_parking';

/**
 * Categories shown in the v2 guidebook editor. Backed by AddressModel where possible
 * (no data duplication) and by predefined custom_* address_settings keys otherwise.
 * Custom (free-form) sections are kept as a separate group below this list.
 */
export const GUIDEBOOK_V2_CATEGORIES: GuidebookV2Category[] = [
  {
    id: 'parking',
    label: 'How to Park',
    icon: 'assets/svg/car-outline.svg',
    fixed: true,
    photoKind: 'parking',
    fields: [
      { key: PAID_PARKING_FIELD_KEY, label: 'Parking type', inputType: 'select', fromAddressModel: true, addressField: 'paid_parking', options: PARKING_TYPE_OPTIONS },
      { key: 'parking_spot', label: 'Parking spot', inputType: 'select', fromAddressModel: true, addressField: 'parking_spot', options: FREE_PARKING_SPOT_OPTIONS, optionsWhenPaid: PAID_PARKING_SPOT_OPTIONS },
      { key: 'parking_pay_with', label: 'Payment method', inputType: 'select', fromAddressModel: true, addressField: 'parking_pay_with', options: PARKING_PAY_OPTIONS, onlyWhenPaid: true },
      { key: 'max_parking_cost', label: 'Max parking cost', inputType: 'select', fromAddressModel: true, addressField: 'max_parking_cost', options: MAX_PARKING_COST_OPTIONS, onlyWhenPaid: true },
      { key: 'parking_notes', label: 'Parking notes', inputType: 'textarea', fromAddressModel: true, addressField: 'parking_notes' },
    ],
  },
  {
    id: 'access',
    label: 'How to Get In',
    icon: 'assets/svg/key-outline.svg',
    fixed: true,
    photoKind: 'access',
    fields: [
      { key: 'home_access', label: 'How to get in', inputType: 'textarea', fromAddressModel: true, addressField: 'home_access' },
    ],
  },
  {
    id: 'connectivity',
    label: 'Connectivity',
    icon: 'assets/svg/wifi-outline.svg',
    fields: [
      { key: 'wifi_network', label: 'WiFi network', fromAddressModel: true, addressField: 'wifi_network' },
      { key: 'wifi_password', label: 'WiFi password', fromAddressModel: true, addressField: 'wifi_password' },
      { key: 'custom_smart_lock_app', label: 'Smart lock app' },
      { key: 'custom_router_location', label: 'Router location' },
    ],
  },
  {
    id: 'stay_details',
    label: 'Stay details',
    icon: 'assets/svg/calendar-outline.svg',
    fields: [
      { key: 'custom_check_in_time', label: 'Check-in time' },
      { key: 'custom_check_out_time', label: 'Check-out time' },
      { key: 'custom_max_occupancy', label: 'Max occupancy' },
      { key: 'custom_pets_policy', label: 'Pets policy' },
      { key: 'custom_noise_curfew', label: 'Noise curfew' },
      { key: 'custom_visitor_policy', label: 'Visitor policy', inputType: 'textarea' },
      { key: 'custom_early_late_checkin', label: 'Early / late check-in', inputType: 'textarea' },
    ],
  },
  {
    id: 'emergencies',
    label: 'Emergencies',
    icon: 'assets/svg/alert-circle-outline.svg',
    fields: [
      { key: 'custom_water_shutoff', label: 'Water shutoff location' },
      { key: 'custom_breaker_box', label: 'Breaker box location' },
      { key: 'custom_smoke_co_detectors', label: 'Smoke / CO detectors' },
      { key: 'custom_fire_extinguisher', label: 'Fire extinguisher location' },
      { key: 'custom_hvac_location', label: 'HVAC location' },
    ],
  },
  {
    id: 'contacts',
    label: 'Contacts',
    icon: 'assets/svg/call-outline.svg',
    fields: [],
  },
  {
    id: 'property_reference',
    label: 'Property reference',
    icon: 'assets/svg/information-circle-outline.svg',
    fields: [
      { key: 'custom_trash_day', label: 'Trash / recycling day' },
      { key: 'custom_off_limits', label: 'Off-limits areas', inputType: 'textarea' },
      { key: 'custom_hoa_quiet_hours', label: 'HOA quiet hours' },
      { key: 'custom_smoking_policy', label: 'Smoking policy' },
    ],
  },
  {
    id: 'appliances',
    label: 'Appliances & amenities',
    icon: 'assets/svg/tv-outline.svg',
    fields: [
      { key: 'custom_tv_instructions', label: 'TV / remote', inputType: 'textarea' },
      { key: 'custom_thermostat', label: 'Thermostat', inputType: 'textarea' },
      { key: 'custom_washer_dryer', label: 'Washer / dryer', inputType: 'textarea' },
      { key: 'custom_bbq', label: 'BBQ', inputType: 'textarea' },
      { key: 'custom_pool_hot_tub', label: 'Pool / hot tub', inputType: 'textarea' },
    ],
  },
  {
    id: 'checkout',
    label: 'Checkout checklist',
    icon: 'assets/svg/log-out-outline.svg',
    fields: [
      { key: 'check_out_instructions', label: 'Checkout instructions', inputType: 'textarea' },
      { key: 'custom_checkout_checklist', label: 'Checkout checklist', inputType: 'textarea' },
    ],
  },
];

/** Returns true when a key was registered as a v2 predefined custom_* setting. */
export function isPredefinedV2Key(key: string): boolean {
  for (const category of GUIDEBOOK_V2_CATEGORIES) {
    for (const field of category.fields) {
      if (field.key === key && !field.fromAddressModel) {return true;}
    }
  }
  return false;
}

export function isCustomSectionKey(key: string): boolean {
  return key.startsWith(CUSTOM_SECTION_KEY_PREFIX);
}

export function slugifyCustomLabel(label: string): string {
  const slug = (label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  const safeSlug = slug || `section_${Date.now()}`;
  return `${CUSTOM_SECTION_KEY_PREFIX}${safeSlug}`;
}

@Injectable({ providedIn: 'root' })
export class AddressGuidebookService {

  private http = inject(HttpClient);

  async getAddressWithSettings(
    addressId: number,
    extraIncludes: string[] = []
  ): Promise<AddressWithSettings> {
    const includes = this.buildIncludes(extraIncludes);
    const url = `${environment.api_url}api/v1/customer/addresses/${addressId}?includes=${includes}`;
    return firstValueFrom(this.http.get<AddressWithSettings>(url));
  }

  async listAddressesWithSettings(extraIncludes: string[] = []): Promise<AddressWithSettings[]> {
    const includes = this.buildIncludes(extraIncludes);
    const url = `${environment.api_url}api/v1/customer/addresses?per_page=5000&includes=${includes}`;
    return firstValueFrom(this.http.get<AddressWithSettings[]>(url));
  }

  async listReservationsForAddress(
    addressId: number,
    state: 'active' | 'inactive' | 'all' = 'active',
    perPage = 25,
    includes: string[] = []
  ): Promise<ReservationSummary[]> {
    const url = this.reservationsUrl(addressId, state, perPage, includes);
    return firstValueFrom(this.http.get<ReservationSummary[]>(url));
  }

  /** One page of the address's stays, latest check-in first, and whether another page follows. */
  async listReservationsPageForAddress(
    addressId: number,
    state: 'active' | 'inactive' | 'all',
    page: number,
    perPage: number,
    includes: string[] = []
  ): Promise<{ reservations: ReservationSummary[]; hasNextPage: boolean }> {
    const url = this.reservationsUrl(addressId, state, perPage, includes, page);
    const response = await firstValueFrom(this.http.get<ReservationSummary[]>(url, { observe: 'response' }));
    return {
      reservations: response.body ?? [],
      hasNextPage: response.headers.get('X-Has-Next-Page') === 'true',
    };
  }

  private reservationsUrl(
    addressId: number,
    state: 'active' | 'inactive' | 'all',
    perPage: number,
    includes: string[],
    page?: number
  ): string {
    const params = new URLSearchParams({
      address_id: String(addressId),
      per_page: String(perPage),
      // Real bookings only — the backend also returns inquiries when omitted.
      type: 'reservation',
    });
    if (page) {params.set('page', String(page));}
    if (state !== 'all') {params.set('state', state);}
    // `access_codes` comes back only when asked for, and only on stays at addresses the member can see.
    if (includes.length) {params.set('includes', includes.join(','));}
    return `${environment.api_url}api/v1/customer/guest-reservations?${params.toString()}`;
  }

  async upsertGuidebook(
    addressId: number,
    settings: UpsertGuidebookEntry[],
    extraIncludes: string[] = []
  ): Promise<AddressWithSettings> {
    const includes = this.buildIncludes(extraIncludes);
    const url = `${environment.api_url}api/v1/customer/addresses/${addressId}/address-settings?includes=${includes}`;
    // Tag every entry with its domain — the backend requires a top-level `category` per setting.
    const body = settings.map(setting => ({ category: GUIDEBOOK_SETTING_CATEGORY, ...setting }));
    return firstValueFrom(this.http.put<AddressWithSettings>(url, { settings: body }));
  }

  /**
   * Removes guidebook settings outright, returning 204. Replaces the old
   * `data: null` soft-delete, which the backend now rejects since `data` is
   * required on every upsert entry. Nothing in the guidebook category is
   * platform-synced, so every key here deletes normally.
   */
  async deleteGuidebookSettings(addressId: number, keys: string[]): Promise<void> {
    if (!keys.length) { return; }
    const params = new URLSearchParams({ category: GUIDEBOOK_SETTING_CATEGORY });
    for (const key of keys) { params.append('keys[]', key); }
    const url = `${environment.api_url}api/v1/customer/addresses/${addressId}/address-settings?${params.toString()}`;
    await firstValueFrom(this.http.delete(url));
  }

  toMap(settings: AddressSetting[] | undefined): Record<string, Record<string, unknown>> {
    const map: Record<string, Record<string, unknown>> = {};
    for (const setting of settings || []) {
      map[setting.key] = setting.data || {};
    }
    return map;
  }

  private buildIncludes(extraIncludes: string[]): string {
    const merged = ['address_settings', ...extraIncludes];
    return Array.from(new Set(merged)).join(',');
  }
}
